import React, { useEffect, useState, useCallback } from "react";
import { headers, restApiUrl, ptw_can_manage_contractors, ptw_can_approve_contractors } from "./app_props";
import { usePopup } from "../components/PopupProvider";

const EMPTY_FORM = {
  companyName: "",
  contactPerson: "",
  contactPhone: "",
  contactEmail: "",
  safetyRegNumber: "",
  siteEntryAuthExpiry: "",
  remarks: "",
};

export default function Contractors() {
  const { showError, showSuccess, showConfirm } = usePopup();
  const header = headers();
  const user = JSON.parse(sessionStorage.getItem("userInfo"));
  const canManage = user?.authorities?.includes(ptw_can_manage_contractors);
  const canApprove = user?.authorities?.includes(ptw_can_approve_contractors);

  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${restApiUrl}/api/contractors`, { method: "GET", mode: "cors", headers: header })
      .then((r) => r.json())
      .then((j) => setContractors(Array.isArray(j) ? j : []))
      .catch(() => showError("Failed to load contractors."))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      companyName: c.companyName || "",
      contactPerson: c.contactPerson || "",
      contactPhone: c.contactPhone || "",
      contactEmail: c.contactEmail || "",
      safetyRegNumber: c.safetyRegNumber || "",
      siteEntryAuthExpiry: c.siteEntryAuthExpiry ? c.siteEntryAuthExpiry.slice(0, 16) : "",
      remarks: c.remarks || "",
    });
    setShowForm(true);
  };

  const handleSave = () => {
    if (!form.companyName.trim()) { showError("Company name is required."); return; }
    setSaving(true);
    const url = editId
      ? `${restApiUrl}/api/contractors/${editId}`
      : `${restApiUrl}/api/contractors`;
    const method = editId ? "PUT" : "POST";
    const body = { ...form };
    if (body.siteEntryAuthExpiry) body.siteEntryAuthExpiry = body.siteEntryAuthExpiry + ":00";

    fetch(url, { method, mode: "cors", headers: header, body: JSON.stringify(body) })
      .then((r) => r.json())
      .then((j) => {
        if (j && j.id) {
          showSuccess(editId ? "Contractor updated." : "Contractor created.");
          setShowForm(false);
          load();
        } else {
          showError(j.message || "Failed to save contractor.");
        }
      })
      .catch(() => showError("Network error."))
      .finally(() => setSaving(false));
  };

  const handleApprove = async (id) => {
    const confirmed = await showConfirm("Approve this contractor for site entry?", { title: "Approve Contractor" });
    if (!confirmed) return;
    fetch(`${restApiUrl}/api/contractors/${id}/approve`, { method: "POST", mode: "cors", headers: header })
      .then((r) => r.json())
      .then(() => { showSuccess("Contractor approved."); load(); })
      .catch(() => showError("Failed to approve contractor."));
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm("Remove this contractor?", { title: "Remove Contractor", severity: "error" });
    if (!confirmed) return;
    fetch(`${restApiUrl}/api/contractors/${id}`, { method: "DELETE", mode: "cors", headers: header })
      .then(() => { showSuccess("Contractor removed."); load(); })
      .catch(() => showError("Failed to remove contractor."));
  };

  const authBadge = (c) => {
    if (!c.approved) return <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Not Approved</span>;
    if (c.siteAuthValid) return <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Auth Valid</span>;
    return <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Auth Expired</span>;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Contractor Management</h1>
        {canManage && (
          <button onClick={openCreate}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm">
            + Add Contractor
          </button>
        )}
      </div>

      {/* Form panel */}
      {showForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            {editId ? "Edit Contractor" : "New Contractor"}
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 md:col-span-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">Company Name *</label>
              <input className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
                value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Contact Person</label>
              <input className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
                value={form.contactPerson} onChange={(e) => setForm({ ...form, contactPerson: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Contact Phone</label>
              <input className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
                value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Contact Email</label>
              <input type="email" className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
                value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Safety Reg. Number</label>
              <input className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
                value={form.safetyRegNumber} onChange={(e) => setForm({ ...form, safetyRegNumber: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Site Entry Auth. Expiry</label>
              <input type="datetime-local" className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
                value={form.siteEntryAuthExpiry} onChange={(e) => setForm({ ...form, siteEntryAuthExpiry: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
              <textarea rows={2} className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
                value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleSave} disabled={saving}
              className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50">
              {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={() => setShowForm(false)}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-200">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <p className="text-gray-500 text-sm">Loading…</p>
      ) : contractors.length === 0 ? (
        <p className="text-gray-500 text-sm italic">No contractors registered yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {["Company", "Contact", "Safety Reg.", "Site Auth Expiry", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {contractors.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{c.companyName}</div>
                    {c.remarks && <div className="text-xs text-gray-500">{c.remarks}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {c.contactPerson && <div>{c.contactPerson}</div>}
                    {c.contactPhone && <div className="text-xs">{c.contactPhone}</div>}
                    {c.contactEmail && <div className="text-xs">{c.contactEmail}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{c.safetyRegNumber || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {c.siteEntryAuthExpiry
                      ? c.siteEntryAuthExpiry.replace("T", " ").slice(0, 16)
                      : "—"}
                  </td>
                  <td className="px-4 py-3">{authBadge(c)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {canManage && (
                        <button onClick={() => openEdit(c)}
                          className="text-blue-600 hover:text-blue-800 text-xs">Edit</button>
                      )}
                      {canApprove && !c.approved && (
                        <button onClick={() => handleApprove(c.id)}
                          className="text-green-600 hover:text-green-800 text-xs">Approve</button>
                      )}
                      {canManage && (
                        <button onClick={() => handleDelete(c.id)}
                          className="text-red-500 hover:text-red-700 text-xs">Remove</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
